<template>
  <v-card outlined>
    <v-card-title>{{ `${$t('sod.ttl.matrix')} ${$t('common.define')}` }}</v-card-title>
    <v-card-text class="pa-2 pt-0">
      <v-row>
        <!-- eslint-disable-next-line prettier/prettier -->
        <v-col cols="2"><p class="label">{{ `${$t('sod.ttl.matrix')} ${$t('sod.ttl.nm')}` }}</p></v-col>
        <v-col cols="4"><v-text-field v-model="item.matrixNm" outlined dense hide-details :disabled="isNotEditable"/></v-col>
        <!-- eslint-disable-next-line prettier/prettier -->
        <v-col cols="1"><p class="label">{{ $t('sod.ttl.risk.level') }}</p></v-col>
        <v-col cols="2">
          <!-- eslint-disable-next-line prettier/prettier -->
          <v-select
            v-model="item.riskLevel" :items="riskLevels" item-text="title" item-value="value" class="w-auto"
            :placeholder="this.$t('common.noSelect')" :menu-props="{ offsetY: true }"
            outlined dense hide-details :disabled="isNotEditable"
          />
        </v-col>
        <!-- eslint-disable-next-line prettier/prettier -->
        <v-col cols="1"><p class="label">{{ $t('sod.ttl.is.active') }}</p></v-col>
        <v-col cols="2"><v-checkbox v-model="item.isActive" :true-value="1" :false-value="0" dense hide-details/></v-col>
        <!-- eslint-disable-next-line prettier/prettier -->
        <v-col cols="2"><p class="label">{{ $t('sod.ttl.row.duty') }}</p></v-col>
        <v-col cols="4">
          <!-- eslint-disable-next-line prettier/prettier -->
          <v-autocomplete
            v-model="item.rowDutyId" :items="rowDutyList" item-text="dutyNm" item-value="dutyId"
            :label="$t('sod.ph.duty.nm')" clearable dense flat hide-no-data solo hide-details
            :disabled="isNotEditable"
            @update:search-input="searchRowDutyList"
          />
        </v-col>
        <!-- eslint-disable-next-line prettier/prettier -->
        <v-col cols="2"><p class="label">{{ $t('sod.ttl.col.duty') }}</p></v-col>
        <v-col cols="4">
          <!-- eslint-disable-next-line prettier/prettier -->
          <v-autocomplete
            v-model="item.colDutyId" :items="colDutyList" item-text="dutyNm" item-value="dutyId"
            :label="$t('sod.ph.duty.nm')" clearable dense flat hide-no-data solo hide-details
            :disabled="isNotEditable"
            @update:search-input="searchColDutyList"
          />
        </v-col>
        <!-- eslint-disable-next-line prettier/prettier -->
        <v-col cols="2"><p class="label">{{ `${$t('sod.ttl.matrix')} ${$t('sod.ttl.desc')}` }}</p></v-col>
        <v-col cols="10"><v-text-field v-model="item.matrixDesc" outlined dense hide-details :disabled="isNotEditable"/></v-col>
      </v-row>
    </v-card-text>
    <v-card-actions>
      <v-spacer></v-spacer>
      <v-btn class="mr-2" :disabled="!isSavable" small color="primary" @click="saveMatrix">{{ $t('common.save') }}</v-btn>
      <v-btn class="mr-2" small color="primary" @click="cancelMatrix">{{ $t('common.cancel') }}</v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import { isEmpty } from 'lodash';

import { api } from '@/api/common/instances';
import { SOD_RISK_LEVEL } from '@/constants';

export default {
  props: {
    matrixId: {
      type: Number,
    },
  },
  data() {
    return {
      item: {
        rowDutyId: 0,
        colDutyId: 0,
      },
      rowDutyList: [],
      colDutyList: [],
      riskLevels: SOD_RISK_LEVEL(),
    };
  },
  computed: {
    isSavable() {
      // eslint-disable-next-line prettier/prettier
      return !isEmpty(this.item.matrixNm)
        && !isEmpty(this.item.riskLevel)
        && this.item.rowDutyId != 0
        && this.item.colDutyId != 0;
    },
    isNotEditable() {
      return this.item.isSstm == 1 ? true : false;
    },
  },
  watch: {
    matrixId: {
      handler(val) {
        if (val == 0) this.item = { rowDutyId: 0, colDutyId: 0 };
        else this.fetchMatrix();
      },
    },
  },
  methods: {
    async searchRowDutyList(input) {
      /*
       * <v-autocomplete> 컴포넌트의 item-text와 item-value를 사용하고 있는 상황이어서
       * input 값을 체크하는 로직이 복잡하다.
       */
      const inputDuty = this.rowDutyList.length == 0 ? [] : this.rowDutyList.find(rd => rd.dutyId == this.item.rowDutyId);
      /*
       * <v-autocomplete> 컴포넌트가 created() 되면 초기값이 null이어서 list가 빈 array이어서 무한루프가 발생한다.
       * 또한, 값이 변하지 않은 상태이면 list를 조회하러 갈 필요없다.
       */
      if (input === null || input == inputDuty?.dutyNm) return;
      this.rowDutyList = await this.autoSearch({ dutyNm: input.toUpperCase() });
    },
    async searchColDutyList(input) {
      const inputDuty = this.colDutyList.length == 0 ? [] : this.colDutyList.find(cd => cd.dutyId == this.item.colDutyId);
      if (input === null || input == inputDuty?.dutyNm) return;
      this.colDutyList = await this.autoSearch({ dutyNm: input.toUpperCase() });
    },
    async autoSearch(params) {
      const { data: items } = await api.get('/sod/auto-search/duty-nm', { params });
      return items;
    },
    async saveMatrix() {
      if (await this.$dialog.confirm(this.$t('common.save.q'))) {
        const { status } = await api.post('/sod/matrix', this.item);
        if (status === 200) {
          this.$snackbar(this.$t('common.successful'));
          this.$emit('close', 'save');
          this.item = {};
        }
      }
    },
    cancelMatrix() {
      this.$emit('close', 'cancel');
      this.item = {};
    },
    async fetchMatrix() {
      const params = { matrixId: this.matrixId };
      const { data: item } = await api.get('/sod/matrix', { params });
      this.item = item;
      /*
       * Edit 모드에서 <v-autocomplet>에 현재 선택된 값이 표시되게 하려면
       * 그 값이 list에 들어 있어야 한다.
       */
      this.rowDutyList.push({ dutyId: item.rowDutyId, dutyNm: item.rowDutyNm });
      this.colDutyList.push({ dutyId: item.colDutyId, dutyNm: item.colDutyNm });
    },
  },
};
</script>

<style scoped>
.label {
  text-align: right;
  padding: 4px 15px 0px 0px;
}
.v-text-field {
  width: 100%;
}
</style>
